import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import { SalvButton, SalvTextArea, SalvTooltipWrapper, Size } from '@salvapp/ui-components';
import { displayApplicable, useAlertStatuses } from '../../screening/alert-status/alertStatusesHelper';
import { alertStatusApi, noteInternalApi } from '../../apis';
import {
  InternalApiAlertType,
  InternalApiDraftReferenceType,
  InternalApiEntityType,
  InternalApiScreeningAlertStatus,
} from '../../api-internal';
import { AlertNotes } from './notes/AlertNotes';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons/faPenToSquare';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { AlertAssignment } from './AlertAssignment';
import { useAlertManagement } from './useAlertManagement';
import { AlertStatusSelection } from './AlertStatusSelection';
import { AlertHistory } from './AlertHistory';
import AlertNoteTemplateDropdown from '../note-templates/AlertNoteTemplateDropdown';
import './AlertManagement.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import store from '../../store/store';
import Has from '../../auth/Has';
import { CAN_USE_ALERT_NOTE_TEMPLATE } from '../../auth/permissions';
import { useNoteDrafts } from '../../note-drafts';
import { NOTE_DRAFTS_QUERY_KEY, useDeleteNoteDraft, useQueryNoteDrafts } from '../../note-drafts';
import { DeleteNoteDraftDialog } from '../../note-drafts';
import { NoteDraftConflictDialog } from '../../note-drafts';
import { NoteEditor, NoteSubmitOptions } from './notes/NoteEditor';
import { useMe } from '../../organisation/useMe';

interface Props {
  alertType: InternalApiAlertType;
  alertSubType?: string;
  customListId?: string;
  alertId: number;
  entityType: InternalApiEntityType;
  preStatusChangeValidation?: () => Promise<boolean>;
  preStatusChange?: (change: AlertPropertyChange) => Promise<boolean>;
  postAlertPropertyChange?: (change: AlertPropertyChange) => void;
}

export interface AlertPropertyChange {
  oldStatus?: InternalApiScreeningAlertStatus;
  newStatus?: InternalApiScreeningAlertStatus;
  newNote?: string;
  newAssignee?: number;
}

interface StatusChange {
  newStatus?: string;
  newNote?: string;
}

interface SubmitNoteParams {
  content?: string;
  status?: string;
  draftId?: number;
  resetDraftOnSuccess?: boolean;
  clearPendingOnValidationFail?: boolean;
}

const getReferenceType = (alertType: InternalApiAlertType): InternalApiDraftReferenceType => {
  switch (alertType) {
    case InternalApiAlertType.MONITORING:
      return InternalApiDraftReferenceType.MONITORING_ALERT;
    case InternalApiAlertType.SCREENING:
      return InternalApiDraftReferenceType.SCREENING_ALERT;
    case InternalApiAlertType.BRIDGE:
      return InternalApiDraftReferenceType.BRIDGE_ALERT;
    case InternalApiAlertType.MANUAL:
      return InternalApiDraftReferenceType.MANUAL_ALERT;
  }
};

export const AlertManagement = ({
  alertType,
  alertSubType,
  customListId,
  alertId,
  entityType,
  preStatusChangeValidation,
  preStatusChange = confirmTPChange,
  postAlertPropertyChange,
}: Props) => {
  const [isSubmitting, setSubmitting] = useState(false);
  const managementProps = useMemo(() => ({ alertType, alertId }), [alertType, alertId]);
  const [activeStatuses, allStatuses] = useAlertStatuses();
  const [alertState, loadData] = useAlertManagement(managementProps);
  const { currentOrganisation } = useMe();

  const draftReferenceType = getReferenceType(alertType);

  const { data: alertDrafts } = useQueryNoteDrafts(alertId, draftReferenceType);
  const deleteDraftMutation = useDeleteNoteDraft({
    referenceId: alertId,
    referenceType: draftReferenceType,
    organisationId: currentOrganisation?.id,
  });

  const {
    isDraftEnabled,
    organisationId,
    visibleDrafts,
    loadedExistingDraft,
    draftToDelete,
    conflictDraft,
    isTakingOver,
    pendingChange,
    isDraftActiveElsewhere,
    handleDraftIdChange,
    handleEditDraft,
    handleDeleteDraft,
    handleTakeover,
    confirmDeleteDraft,
    setPendingChange,
    setDraftToDelete,
    setConflictDraft,
    resetDraftState,
  } = useNoteDrafts({
    referenceId: alertId,
    referenceType: draftReferenceType,
    drafts: alertDrafts,
    deleteDraft: async (id) => {
      await deleteDraftMutation.mutateAsync({ id });
    },
    draftsQueryKey: NOTE_DRAFTS_QUERY_KEY,
  });

  const selectableStatuses = useMemo(() => {
    return displayApplicable(entityType, activeStatuses, alertType, false, alertSubType, customListId);
  }, [entityType, activeStatuses, alertType, alertSubType, customListId]);

  const change = useCallback(
    (newNote: string | null, newStatus: string | null) => {
      if (newStatus != null) {
        setPendingChange((prev) => ({ ...prev, newStatus }));
      } else if (newNote != null) {
        setPendingChange((prev) => ({ ...prev, newNote }));
      } else if (!pendingChange) {
        setPendingChange({ newNote: '' });
      } else {
        setPendingChange((prev) => ({ ...prev, newStatus: undefined }));
      }
    },
    [pendingChange, setPendingChange]
  );

  const inputRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    pendingChange?.newStatus && inputRef.current?.focus();
  }, [pendingChange?.newStatus]);

  const submitNoteOrStatus = useCallback(
    async ({ content, status, draftId, resetDraftOnSuccess, clearPendingOnValidationFail }: SubmitNoteParams) => {
      const changeEvent = {
        oldStatus: statusById(alertState?.statusEvents?.[0].statusId, allStatuses),
        newStatus: status ? statusByCode(status, activeStatuses) : undefined,
        newNote: content,
      };
      if (preStatusChangeValidation && (await preStatusChangeValidation?.())) {
        if (clearPendingOnValidationFail) {
          setPendingChange(undefined);
        }
        return;
      }
      if (preStatusChange && !(await preStatusChange?.(changeEvent))) {
        if (clearPendingOnValidationFail) {
          setPendingChange(undefined);
        }
        return;
      }
      let refreshStatuses = false;
      if (status && status !== changeEvent.oldStatus?.code) {
        await alertStatusApi.updateAlertStatus({
          internalApiAlertStatusUpdate: {
            alertId,
            alertType,
            status,
            note: content,
            draftId,
          },
        });
        refreshStatuses = true;
      } else if (content) {
        await noteInternalApi.createNote({
          internalApiCreateNote: {
            alertId,
            alertType: InternalApiAlertType[alertType],
            content,
            draftId,
          },
        });
      }

      await loadData(true, refreshStatuses, false);
      setPendingChange(undefined);
      if (resetDraftOnSuccess) {
        resetDraftState();
      }
      postAlertPropertyChange?.(changeEvent);
    },
    [
      alertState,
      allStatuses,
      activeStatuses,
      preStatusChangeValidation,
      preStatusChange,
      alertId,
      alertType,
      loadData,
      resetDraftState,
      postAlertPropertyChange,
      setPendingChange,
    ]
  );

  const submitAssignmentChange = useCallback(
    async (assigneeId: number | null) => {
      await loadData(false, false, true);
      postAlertPropertyChange?.({ newAssignee: assigneeId ?? undefined });
    },
    [postAlertPropertyChange, loadData]
  );

  const submitStatusChange = useCallback(
    async (change: StatusChange) => {
      await submitNoteOrStatus({
        content: change.newNote,
        status: change.newStatus,
        clearPendingOnValidationFail: true,
      });
    },
    [submitNoteOrStatus]
  );

  const handleNoteSubmit = useCallback(
    async (content: string, status?: string, options?: NoteSubmitOptions) => {
      await submitNoteOrStatus({
        content,
        status,
        draftId: options?.draftId,
        resetDraftOnSuccess: true,
      });
    },
    [submitNoteOrStatus]
  );

  const handleNoteCancel = useCallback(() => {
    setPendingChange(undefined);
    resetDraftState();
  }, [resetDraftState, setPendingChange]);

  return (
    <>
      <Card className='alert-management-status-assignment p-a-16'>
        <div className='status-block'>
          <span className='caps-xs p-b-4' style={{ color: 'var(--secondary-default)' }}>
            Alert Status
          </span>
          <SalvTooltipWrapper tooltip='Set status' shortcut={'S'} placement={'right'}>
            <AlertStatusSelection
              label={statusById(alertState?.statusEvents?.[0].statusId, allStatuses)?.textName ?? 'Set status'}
              size={Size.MEDIUM}
              showNotes={true}
              selectableStatuses={selectableStatuses}
              selectStatus={(status) => change(null, status)}
              selectNote={(status, note) => submitStatusChange({ newStatus: status, newNote: note })}
              shortcutTriggerKey={'s'}
            />
          </SalvTooltipWrapper>
        </div>
        <div className='spacer' />
        {alertState && (
          <>
            <AlertAssignment
              alertId={alertId}
              alertType={alertType}
              alertState={alertState}
              onChange={submitAssignmentChange}
            />
            <span className='m-r-8' />
            <AlertHistory alertId={alertId} alertType={alertType} alertState={alertState} alertStatuses={allStatuses} />
          </>
        )}
      </Card>
      <AlertNotes
        notes={alertState?.notes ?? []}
        statusChanges={alertState?.statusEvents ?? []}
        drafts={isDraftEnabled ? visibleDrafts : undefined}
        onEditDraft={isDraftEnabled ? handleEditDraft : undefined}
        onDeleteDraft={isDraftEnabled ? handleDeleteDraft : undefined}
        isDraftActiveElsewhere={isDraftEnabled ? isDraftActiveElsewhere : undefined}
        cardAction={
          !pendingChange && (
            <SalvButton
              label='Add note'
              size={Size.SMALL}
              variant='secondary'
              icon={faPenToSquare}
              onClick={() => change(null, null)}
            />
          )
        }
      >
        {pendingChange && isDraftEnabled && organisationId && (
          <NoteEditor
            alertType={alertType}
            referenceId={alertId}
            referenceType={draftReferenceType}
            existingDraft={loadedExistingDraft}
            selectableStatuses={selectableStatuses}
            initialStatus={pendingChange.newStatus}
            isDraftEnabled={isDraftEnabled}
            onSubmit={handleNoteSubmit}
            onCancel={handleNoteCancel}
            onDraftIdChange={handleDraftIdChange}
          />
        )}
        {pendingChange && !isDraftEnabled && (
          <div className='divider p-a-16 new-note'>
            <SalvTextArea
              ref={inputRef}
              ariaLabel='New note content'
              placeholder='Enter your note here...'
              value={pendingChange?.newNote ?? ''}
              onChange={(newNote) => change(newNote, null)}
              size={Size.EXTRA_SMALL}
              autoSizing={true}
            />
            <div className='p-t-16 note-controls'>
              <Has allPermissions={CAN_USE_ALERT_NOTE_TEMPLATE}>
                <SalvTooltipWrapper tooltip='Insert note template'>
                  <AlertNoteTemplateDropdown
                    alertType={alertType}
                    targetElement={inputRef}
                    onTemplateSelect={(template) => change(template.content, null)}
                    disabled={false}
                  />
                </SalvTooltipWrapper>
              </Has>
              <SalvTooltipWrapper tooltip={'Optional selection to add status change with this note'}>
                <AlertStatusSelection
                  label={statusByCode(pendingChange.newStatus, activeStatuses)?.textName ?? 'Add status'}
                  icon={pendingChange.newStatus ? undefined : faPlus}
                  size={Size.SMALL}
                  showNotes={false}
                  selectableStatuses={selectableStatuses}
                  selectStatus={(status) => change(null, status)}
                  selectNote={() => null}
                />
              </SalvTooltipWrapper>
              {pendingChange.newStatus && (
                <FontAwesomeIcon
                  aria-label='Remove status assignment'
                  size='lg'
                  className='m-l-8'
                  icon={faXmark}
                  onClick={() => change(null, null)}
                />
              )}
              <div className='spacer' />
              <SalvButton
                className='m-r-8'
                label='Cancel'
                variant='muted'
                size={Size.SMALL}
                onClick={() => setPendingChange(undefined)}
              />
              <SalvButton
                label='Submit'
                size={Size.SMALL}
                disabled={!pendingChange.newNote || isSubmitting}
                onClick={async () => {
                  setSubmitting(true);
                  try {
                    await submitStatusChange(pendingChange);
                  } finally {
                    setSubmitting(false);
                  }
                }}
              />
            </div>
          </div>
        )}
      </AlertNotes>
      {draftToDelete && (
        <DeleteNoteDraftDialog
          draft={draftToDelete}
          show={!!draftToDelete}
          onClose={() => setDraftToDelete(null)}
          onConfirm={confirmDeleteDraft}
        />
      )}
      <NoteDraftConflictDialog
        isOpen={conflictDraft !== null}
        onTakeover={handleTakeover}
        onCancel={() => setConflictDraft(null)}
        isTakingOver={isTakingOver}
      />
    </>
  );
};

const confirmTPChange = async (statusChange: AlertPropertyChange): Promise<boolean> => {
  if (!statusChange.newStatus?.isImportant) {
    return true;
  }
  return new Promise((resolve) => {
    store.dispatch(
      showConfirmationModal({
        title: `Are you sure you want to change the status to "${statusChange.newStatus?.textName}"?`,
        onConfirm() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        },
        confirmationText: 'Yes',
        cancelText: 'No, keep previous status',
      })
    );
  });
};

const statusById = (statusId: number | undefined, statuses: InternalApiScreeningAlertStatus[]) => {
  return statuses.find((s) => s.id === statusId);
};
const statusByCode = (statusCode: string | undefined, statuses: InternalApiScreeningAlertStatus[]) => {
  return statuses.find((s) => s.code === statusCode);
};
